import { useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { GridColDef } from "@mui/x-data-grid";
import { ConfirmDialog, ServerDataGrid, formatCnpj, formatRelativeFromNow, useDataGridState } from "@safrater/ui";
import { Link as MuiLink } from "@mui/material";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface Row {
  id: string;
  name: string;
  cnpj: string;
  status: "active" | "pending_approval" | "suspended";
  email: string | null;
  phone: string | null;
  createdAt: string;
  _count: { memberships: number; licenses: number };
}

interface FormValues { name: string; email: string; phone: string }

const STATUS_COLORS: Record<string, "success" | "warning" | "error" | "default"> = {
  active: "success", pending_approval: "warning", suspended: "error",
};

export function InstitutionsPage() {
  const state = useDataGridState();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = !!user?.isMasterAdmin;
  const [dlg, setDlg] = useState<{ open: boolean; row?: Row }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<Row | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["institutions", state.queryParams],
    queryFn: async () => (await api.get<{ data: Row[]; total: number }>("/institutions", { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["institutions"] });
  const updateM = useMutation({
    mutationFn: ({ id, v }: { id: string; v: FormValues }) => api.patch(`/institutions/${id}`, {
      name: v.name.trim(), email: v.email.trim() || undefined, phone: v.phone.trim() || undefined,
    }),
    onSuccess: () => { invalidate(); setDlg({ open: false }); },
  });
  const setStatusM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "suspended" }) => api.patch(`/institutions/${id}/status`, { status }),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/institutions/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });

  const columns: GridColDef<Row>[] = [
    {
      field: "name", headerName: "Nome", flex: 1, minWidth: 240,
      renderCell: ({ row }) => (
        <MuiLink component="button" type="button" underline="hover" onClick={() => navigate(`/institutions/${row.id}`)} sx={{ textAlign: "left" }}>
          {row.name}
        </MuiLink>
      ),
    },
    { field: "cnpj", headerName: "CNPJ", width: 200, valueFormatter: (v) => formatCnpj(v as string) },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    {
      field: "status", headerName: "Status", width: 160,
      renderCell: ({ value }) => <Chip size="small" label={value as string} color={STATUS_COLORS[value as string] ?? "default"} />,
    },
    { field: "memberships", headerName: "Usuários", width: 110, type: "number", sortable: false, valueGetter: (_v, row) => row._count.memberships },
    { field: "createdAt", headerName: "Cadastrada", width: 160, type: "date", valueGetter: (v) => (v ? new Date(v as string) : null), renderCell: ({ value }) => (value ? formatRelativeFromNow((value as Date).toISOString()) : "") },
    ...(isMaster ? [{
      field: "_actions", headerName: "Ações", width: 160, sortable: false, filterable: false,
      renderCell: ({ row }: { row: Row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ open: true, row })}><EditIcon fontSize="small" /></IconButton></Tooltip>
          {row.status === "active" ? (
            <Tooltip title="Suspender"><IconButton size="small" color="warning" onClick={() => setStatusM.mutate({ id: row.id, status: "suspended" })}><BlockIcon fontSize="small" /></IconButton></Tooltip>
          ) : row.status === "suspended" ? (
            <Tooltip title="Reativar"><IconButton size="small" color="success" onClick={() => setStatusM.mutate({ id: row.id, status: "active" })}><CheckIcon fontSize="small" /></IconButton></Tooltip>
          ) : null}
          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => setConfirmDelete(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      ),
    } as GridColDef<Row>] : []),
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Instituições</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Para aprovar novas instituições, use a tela "Aprovações". Aqui você gerencia as já cadastradas.
      </Typography>
      <Box sx={{ height: 640 }}>
        <ServerDataGrid<Row>
          gridId="central.institutions"
          userScope={user?.sub}
          rows={data?.data ?? []} rowCount={data?.total ?? 0} columns={columns} loading={isFetching}
          paginationModel={state.pagination} onPaginationModelChange={state.setPagination}
          sortModel={state.sort} onSortModelChange={state.setSort}
          filterModel={state.filter} onFilterModelChange={state.setFilter}
        />
      </Box>
      {dlg.open && dlg.row && (
        <FormDialog key={dlg.row.id} row={dlg.row}
          onClose={() => setDlg({ open: false })}
          onSubmit={(v) => updateM.mutate({ id: dlg.row!.id, v })}
          submitting={updateM.isPending} />
      )}
      <ConfirmDialog
        open={!!confirmDelete} title="Excluir instituição"
        description={`Suspender "${confirmDelete?.name}"? Usuários da instituição perderão acesso.`}
        confirmLabel="Suspender" severity="error" loading={deleteM.isPending}
        onConfirm={() => { if (confirmDelete) deleteM.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}

function FormDialog({ row, onClose, onSubmit, submitting }: { row: Row; onClose: () => void; onSubmit: (v: FormValues) => void; submitting: boolean }) {
  const [v, setV] = useState<FormValues>({ name: row.name, email: row.email ?? "", phone: row.phone ?? "" });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm"
      slotProps={{ paper: { component: "form", onSubmit: (e: React.FormEvent) => { e.preventDefault(); onSubmit(v); } } }}>
      <DialogTitle>Editar instituição</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="CNPJ" value={formatCnpj(row.cnpj)} disabled fullWidth helperText="CNPJ não pode ser alterado" />
          <TextField label="Nome" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Email" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} fullWidth />
            <TextField label="Telefone" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={submitting || !v.name.trim()}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
}
